import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";

export default function LifestylePage() {
  const { t } = useLanguage();
  const [sleep, setSleep] = useState([]);
  const [steps, setSteps] = useState([]);
  const [mood, setMood] = useState([]);
  const [nutrition, setNutrition] = useState([]);
  const [wearable, setWearable] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([
      api.get("/onlyyou/sleep"),
      api.get("/onlyyou/steps"),
      api.get("/onlyyou/mood"),
      api.get("/onlyyou/nutrition"),
      api.get("/onlyyou/wearable"),
    ])
      .then(([s, st, m, n, w]) => {
        setSleep(s.data); setSteps(st.data); setMood(m.data); setNutrition(n.data); setWearable(w.data);
      })
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, []);

  return (
    <div className="space-y-8" data-testid="lifestyle-page">
      <header>
        <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
          {t.layer.onlyyou} · {t.layer.onlyyouSub}
        </div>
        <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-900">
          {t.lifestyle.title}
        </h1>
        <p className="mt-1 text-sm text-slate-500">{t.lifestyle.sub}</p>
      </header>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

      <div className="grid gap-4 lg:grid-cols-2">
        <SeriesTable testid="lifestyle-sleep-table" title={t.twin.sleep} rows={sleep} columns={["date", "duration_hours", "quality"]} />
        <SeriesTable testid="lifestyle-steps-table" title={t.twin.steps} rows={steps} columns={["date", "count", "distance_km"]} />
        <SeriesTable testid="lifestyle-mood-table" title={t.twin.mood} rows={mood} columns={["date", "score", "label"]} />
        <SeriesTable testid="lifestyle-nutrition-table" title={t.twin.nutrition} rows={nutrition} columns={["date", "calories", "protein_g", "carbs_g", "fat_g", "water_l"]} />
      </div>

      <SeriesTable testid="lifestyle-wearable-table" title="Wearable" rows={wearable} columns={["device", "metric", "value", "unit", "captured_at"]} />
    </div>
  );
}

function SeriesTable({ title, rows, columns, testid }) {
  return (
    <div className="mios-card" data-testid={testid}>
      <div className="mios-card-header">
        <div className="mios-card-title">{title}</div>
        <div className="mios-card-sub">{rows.length}</div>
      </div>
      <div className="mios-card-body overflow-x-auto">
        {rows.length === 0 ? (
          <div className="text-sm text-slate-400">—</div>
        ) : (
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-[11px] uppercase tracking-[0.06em] text-slate-500">
                {columns.map((c) => (
                  <th key={c} className="py-2 pr-4 font-medium">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 20).map((r, i) => (
                <tr key={r.id || i} className="border-b border-slate-100 last:border-0">
                  {columns.map((c) => (
                    <td key={c} className="py-2 pr-4 text-slate-700 mono">
                      {r[c] ?? "—"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
